// Ensures the authed clinician has an active clinic membership before
// letting the work-list / patient screens render. No membership → redirect
// to /join-clinic (they redeem an invite code there).
//
// V0 scope: display-only. Actual invite redemption UI = Week 3.

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';
import { clinicApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { AppShell } from './AppShell';

export function ClinicGate({ children }: { children: ReactNode }) {
    const location = useLocation();
    const { data, isLoading, isError } = useQuery({
        queryKey: ['my-clinic'],
        queryFn: () => clinicApi.myClinic(),
    });

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <div className="text-slate-500 text-sm">Loading clinic…</div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex h-full items-center justify-center px-4">
                <div className="pm-card p-6 max-w-md text-center">
                    <div className="text-slate-800 font-medium">Can’t reach the server</div>
                    <div className="text-sm text-slate-500 mt-1">
                        Check your connection and refresh.
                    </div>
                </div>
            </div>
        );
    }

    // No membership → route to /join-clinic (unless already there).
    if (!data?.member || !data?.clinic) {
        if (location.pathname !== '/join-clinic') {
            return <Navigate to="/join-clinic" replace />;
        }
        // Render join-clinic without the app shell (no clinic to name yet).
        return <>{children}</>;
    }

    return <AppShell clinic={data.clinic} member={data.member}>{children}</AppShell>;
}

// Re-export so App.tsx has an ambient useAuth if it ever needs it here.
export { useAuth };
